/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { message, Switch, Spin, Alert } from "antd";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { usersApi } from "../../apis";

const customerEditSchema = yup.object({
  firstName: yup
    .string()
    .required("First name is required")
    .trim(),
  lastName: yup
    .string()
    .required("Last name is required")
    .trim(),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .trim(),
  phone: yup
    .string()
    .nullable()
    .optional()
    .test(
      "phone-format",
      "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
      (value) => {
        if (!value) return true;
        // value here is the local number (without +20)
        // Check if it's exactly 9 digits
        return /^\d{9}$/.test(value);
      },
    ),
  isActive: yup.boolean().optional(),
  password: yup
    .string()
    .optional()
    .min(6, "Minimum 6 characters")
    .transform((value) => (value === "" ? undefined : value)),
});

const EditCustomer = () => {
  const { id } = useParams();
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(customerEditSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isActive: true,
      password: "",
    },
    mode: "onChange",
  });

  const isActive = watch("isActive");

  const extractLocalNumber = (fullPhoneNumber) => {
    if (!fullPhoneNumber) return "";
    if (fullPhoneNumber.startsWith("+20")) {
      return fullPhoneNumber.substring(3);
    }
    return fullPhoneNumber;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    // Remove any non-digit characters
    value = value.replace(/\D/g, "");
    // Limit to 9 digits
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    setLocalPhoneNumber(value);
    setValue("phone", value);

    if (errors.phone) {
      clearErrors("phone");
    }
  };

  useEffect(() => {
    usersApi
      .getById(id)
      .then((u) => {
        // Verify this is a customer user
        const role = (typeof u.role === "string" ? u.role : u.role?.name) || "";
        if (role.toLowerCase() !== "customer") {
          setNotFound(true);
          return;
        }

        setValue("firstName", u.firstName || "");
        setValue("lastName", u.lastName || "");
        setValue("email", u.email || "");

        const fullPhone = u.phone || "";
        const localNumber = extractLocalNumber(fullPhone);
        setValue("phone", localNumber);
        setLocalPhoneNumber(localNumber);

        setValue("isActive", u.isActive !== false);
        setValue("password", "");
      })
      .catch(() => setNotFound(true))
      .finally(() => setFetching(false));
  }, [id, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Validate phone if provided
      if (data.phone) {
        if (!/^\d{9}$/.test(data.phone)) {
          setError("phone", {
            type: "manual",
            message:
              "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
          });
          setLoading(false);
          return;
        }
      }

      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone ? `+20${data.phone}` : undefined,
        role: "customer",
        isActive: data.isActive,
      };

      if (data.password?.trim()) {
        payload.password = data.password.trim();
      }

      await usersApi.update(id, payload);
      message.success("Customer updated successfully");
      history.push("/users/customers");
    } catch (err) {
      const msg = err.response?.data?.message;
      if (Array.isArray(msg)) msg.forEach((m) => message.error(m));
      else message.error(msg || "Failed to update customer");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => {}} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => {}} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert message="Customer user not found." type="error" showIcon />
          </div>
        </div>
      </div>
    );
  }

  const fullName = `${watch("firstName")} ${watch("lastName")}`;

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Edit Customer — {fullName.trim() || "Loading..."}</h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel"
                    onClick={() => history.push("/users/customers")}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="form-group-item">
                  <h5 className="form-title">Personal Information</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className={`form-control ${
                            errors.firstName ? "is-invalid" : ""
                          }`}
                          {...register("firstName")}
                          disabled={loading}
                        />
                        {errors.firstName && (
                          <small className="text-danger d-block mt-1">
                            {errors.firstName.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className={`form-control ${
                            errors.lastName ? "is-invalid" : ""
                          }`}
                          {...register("lastName")}
                          disabled={loading}
                        />
                        {errors.lastName && (
                          <small className="text-danger d-block mt-1">
                            {errors.lastName.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>Phone</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <span className="me-1">🇪🇬</span> +20
                          </span>
                          <input
                            type="tel"
                            className={`form-control ${
                              errors.phone ? "is-invalid" : ""
                            }`}
                            placeholder="1001234567"
                            value={localPhoneNumber}
                            onChange={handlePhoneChange}
                            disabled={loading}
                          />
                        </div>
                        {errors.phone && (
                          <small className="text-danger d-block mt-1">
                            {errors.phone.message}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          Enter 9 digits after +20 (e.g., 1001234567)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group-item mt-2">
                  <h5 className="form-title">Account Details</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          {...register("email")}
                          disabled={loading}
                        />
                        {errors.email && (
                          <small className="text-danger d-block mt-1">
                            {errors.email.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>New Password</label>
                        <input
                          type="password"
                          className={`form-control ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          placeholder="Leave blank to keep current"
                          {...register("password")}
                          disabled={loading}
                          autoComplete="new-password"
                        />
                        {errors.password && (
                          <small className="text-danger d-block mt-1">
                            {errors.password.message}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          Leave blank to keep the current password
                        </small>
                      </div>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <div className="input-block mb-3">
                        <label>Role</label>
                        <input
                          type="text"
                          className="form-control"
                          value="Customer"
                          disabled
                          style={{ backgroundColor: "#f5f5f5" }}
                        />
                      </div>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <div className="input-block mb-3">
                        <label>Account Status</label>
                        <div style={{ paddingTop: 8 }}>
                          <Switch
                            checked={isActive}
                            onChange={(v) => setValue("isActive", v)}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group-item border-0">
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary cancel me-2"
                      onClick={() => history.push("/users/customers")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving…
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCustomer;
